/**
 * LaunchTimeline — week-grid timeline of launch placements.
 *
 * Flexible (soft) bars are horizontally draggable; fixed (hard) bars require
 * unlock first. Drop commits a week delta via onShiftWeeks.
 */

import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import type { TimePhasedPlacement } from '../../../lib/timePhasedPlanning.types';
import { PLACEMENT_STATUS_LABELS } from './timePhasedUiShared';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const COL_W = 34; // px per week column

const BAR_CLASS: Record<TimePhasedPlacement['placementStatus'], string> = {
  scheduled: 'bg-[var(--green)]/80 dark:bg-[var(--green)]/70',
  slipped: 'bg-[var(--mustard)]/80 dark:bg-[var(--mustard)]/70',
  unscheduled: 'bg-[var(--rule-bright)]',
  blocked: 'bg-[var(--coral)]/70',
  supply_exhausted: 'bg-[var(--mustard)]/70',
  below_line: 'bg-[var(--hover-tint)]',
};

function mondayOf(iso: string): Date {
  const d = new Date(iso + 'T00:00:00Z');
  const day = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - ((day + 6) % 7));
  return d;
}

function weekIndex(start: Date, iso: string): number {
  return Math.floor((new Date(iso + 'T00:00:00Z').getTime() - start.getTime()) / WEEK_MS);
}

function fmtWeek(d: Date): string {
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

function fmtMonth(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}

export function LaunchTimeline({
  placements,
  horizonStart,
  horizonEnd,
  onSelect,
  selectedId,
  onShiftWeeks,
}: {
  placements: TimePhasedPlacement[];
  horizonStart?: string;
  horizonEnd?: string;
  onSelect?: (priorityId: string) => void;
  selectedId?: string;
  /** Called when a flexible bar is dragged by ≥1 week. */
  onShiftWeeks?: (priorityId: string, deltaWeeks: number) => void;
}) {
  const dragRef = useRef<{
    priorityId: string;
    startX: number;
    originLeft: number;
  } | null>(null);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const { weeks, rows, todayIdx } = useMemo(() => {
    const dates: string[] = [];
    for (const p of placements) {
      if (p.feasibleStart) dates.push(p.feasibleStart);
      if (p.feasibleEnd) dates.push(p.feasibleEnd);
      if (p.targetLaunchStart) dates.push(p.targetLaunchStart);
      if (p.targetLaunchEnd) dates.push(p.targetLaunchEnd);
    }
    if (horizonStart) dates.push(horizonStart);
    if (horizonEnd) dates.push(horizonEnd);
    if (dates.length === 0) dates.push(new Date().toISOString().slice(0, 10));

    const sorted = [...dates].sort();
    const start = mondayOf(sorted[0]);
    const endIdx = Math.max(weekIndex(start, sorted[sorted.length - 1]), 11);
    const count = Math.min(endIdx + 2, 78);

    const weeks = Array.from({ length: count }, (_, i) => {
      const d = new Date(start.getTime() + i * WEEK_MS);
      return { date: d, isMonthStart: d.getUTCDate() <= 7 };
    });

    const rows = placements.map((p) => {
      const startIdx = p.feasibleStart ? Math.max(0, weekIndex(start, p.feasibleStart)) : null;
      const endI = p.feasibleEnd ? Math.max(startIdx ?? 0, weekIndex(start, p.feasibleEnd)) : startIdx;
      const targetIdx = p.targetLaunchEnd ? weekIndex(start, p.targetLaunchEnd) : null;
      return { p, startIdx, endIdx: endI, targetIdx };
    });

    const todayIdx = weekIndex(start, new Date().toISOString().slice(0, 10));
    return { weeks, rows, todayIdx };
  }, [placements, horizonStart, horizonEnd]);

  if (placements.length === 0) return null;

  const gridW = weeks.length * COL_W;

  const endDrag = (clientX: number) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDraggingId(null);
    setDragOffsetPx(0);
    if (!drag || !onShiftWeeks) return;
    const deltaWeeks = Math.round((clientX - drag.startX) / COL_W);
    if (deltaWeeks !== 0) onShiftWeeks(drag.priorityId, deltaWeeks);
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-max">
        <div className="flex sticky top-0 z-10 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
          <div className="w-52 md:w-64 shrink-0 sticky left-0 z-20 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-b border-r border-[var(--rule)] dark:border-[var(--rule)]" />
          <div className="relative border-b border-[var(--rule)] dark:border-[var(--rule)]" style={{ width: gridW }}>
            <div className="flex">
              {weeks.map((w, i) => (
                <div
                  key={i}
                  className={`shrink-0 text-center pt-1 pb-1.5 ${
                    w.isMonthStart ? 'border-l border-[var(--rule)] dark:border-[var(--rule)]' : ''
                  }`}
                  style={{ width: COL_W }}
                >
                  {w.isMonthStart && (
                    <div className="text-[10px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase">
                      {fmtMonth(w.date)}
                    </div>
                  )}
                  <div className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    {fmtWeek(w.date)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {rows.map(({ p, startIdx, endIdx, targetIdx }) => {
          const isSelected = selectedId === p.priorityId;
          const slipped = p.slipWeeks != null && p.slipWeeks > 0;
          const isFixed = (p.anchoring ?? p.launchFlexibility) === 'fixed';
          const canDrag = !isFixed && Boolean(onShiftWeeks) && startIdx != null;
          const offset = draggingId === p.priorityId ? dragOffsetPx : 0;

          return (
            <div
              key={p.priorityId}
              role={onSelect ? 'button' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              aria-pressed={onSelect ? isSelected : undefined}
              aria-label={`${p.priorityName}, ${PLACEMENT_STATUS_LABELS[p.placementStatus]}${
                isSelected ? ', selected' : ''
              }`}
              className={`flex group ${onSelect ? 'cursor-pointer' : ''} ${
                isSelected ? 'bg-primary-50/60 dark:bg-primary-900/10' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/[0.03]'
              }`}
              onClick={onSelect ? () => onSelect(p.priorityId) : undefined}
              onKeyDown={
                onSelect
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(p.priorityId);
                      }
                    }
                  : undefined
              }
            >
              <div
                className={`w-52 md:w-64 shrink-0 sticky left-0 z-10 px-3 py-2 border-b border-r border-[var(--rule)] dark:border-[var(--rule)] ${
                  isSelected
                    ? 'bg-primary-50 dark:bg-[var(--bg-raised)]'
                    : 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] group-hover:bg-[var(--surface)] dark:group-hover:bg-[var(--bg-raised)]'
                }`}
              >
                <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                  {p.priorityName}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  <span>{PLACEMENT_STATUS_LABELS[p.placementStatus]}</span>
                  {isFixed && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[var(--text-sec)] font-medium"
                      title="Fixed (hard) — unlock before dragging"
                    >
                      <Lock className="h-3 w-3" /> fixed
                    </span>
                  )}
                  {!isFixed && (
                    <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]" title="Flexible (soft) — drag to shift">
                      flexible · drag
                    </span>
                  )}
                  {p.calendarConfidence != null && (
                    <span title="Calendar confidence">{Math.round(p.calendarConfidence * 100)}%</span>
                  )}
                  {slipped && <span className="text-[var(--mustard)] dark:text-[var(--mustard)]">+{p.slipWeeks}wk</span>}
                  {p.buildable && !p.launchable && (
                    <AlertTriangle className="h-3 w-3 text-[var(--mustard)]" aria-label="Buildable but not launchable" />
                  )}
                </div>
              </div>

              <div
                className="relative border-b border-[var(--rule)] dark:border-[var(--rule)]"
                style={{ width: gridW, height: 44 }}
              >
                {weeks.map(
                  (w, i) =>
                    w.isMonthStart && (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 border-l border-[var(--rule)] dark:border-[var(--rule)]"
                        style={{ left: i * COL_W }}
                      />
                    ),
                )}
                {todayIdx >= 0 && todayIdx < weeks.length && (
                  <div
                    className="absolute top-0 bottom-0 border-l-2 border-primary-400/60"
                    style={{ left: todayIdx * COL_W + COL_W / 2 }}
                  />
                )}
                {startIdx != null && endIdx != null && (
                  <div
                    className={`absolute top-2.5 h-5 ${
                      isFixed
                        ? `${BAR_CLASS[p.placementStatus]}  ring-2 ring-slate-800/70 dark:ring-slate-100/70 cursor-not-allowed`
                        : `${BAR_CLASS[p.placementStatus]}  opacity-90 ${canDrag ? 'cursor-grab active:cursor-grabbing touch-none' : ''}`
                    }`}
                    style={{
                      left: startIdx * COL_W + 2 + offset,
                      width: Math.max((endIdx - startIdx + 1) * COL_W - 4, COL_W - 4),
                    }}
                    title={
                      isFixed
                        ? `${p.priorityName}: fixed (hard) — unlock in inspector to move`
                        : `${p.priorityName}: drag to shift launch (soft)`
                    }
                    onPointerDown={
                      canDrag
                        ? (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
                            dragRef.current = {
                              priorityId: p.priorityId,
                              startX: e.clientX,
                              originLeft: startIdx * COL_W + 2,
                            };
                            setDraggingId(p.priorityId);
                            setDragOffsetPx(0);
                          }
                        : undefined
                    }
                    onPointerMove={
                      canDrag
                        ? (e) => {
                            if (!dragRef.current || dragRef.current.priorityId !== p.priorityId) return;
                            setDragOffsetPx(e.clientX - dragRef.current.startX);
                          }
                        : undefined
                    }
                    onPointerUp={
                      canDrag
                        ? (e) => {
                            e.stopPropagation();
                            endDrag(e.clientX);
                          }
                        : undefined
                    }
                    onPointerCancel={
                      canDrag
                        ? () => {
                            dragRef.current = null;
                            setDraggingId(null);
                            setDragOffsetPx(0);
                          }
                        : undefined
                    }
                    onClick={(e) => {
                      // Prevent row toggle when finishing a drag
                      if (Math.abs(offset) > 4) e.stopPropagation();
                    }}
                  />
                )}
                {targetIdx != null && targetIdx >= 0 && targetIdx < weeks.length && (
                  <div
                    className="absolute top-1 bottom-1 w-0.5 bg-[var(--bg-raised)] rounded"
                    style={{ left: targetIdx * COL_W + COL_W / 2 }}
                    title={`Target launch: ${p.targetLaunchEnd}`}
                  />
                )}
                {slipped && targetIdx != null && startIdx != null && startIdx > targetIdx && (
                  <div
                    className="absolute top-[19px] h-0.5 bg-[var(--mustard)]/70"
                    style={{
                      left: targetIdx * COL_W + COL_W / 2,
                      width: (startIdx - targetIdx) * COL_W - COL_W / 2 + 2,
                    }}
                  />
                )}
                {startIdx == null && (
                  <div className="absolute inset-y-0 left-2 flex items-center text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] italic">
                    no feasible window
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-4 bg-[var(--green)]/80" /> Scheduled
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-4 bg-[var(--mustard)]/80" /> Slipped
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Fixed (hard) — unlock to drag
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-4 bg-[var(--slate)]/80" /> Flexible (soft) — drag to shift
          </span>
        </div>
      </div>
    </div>
  );
}
